/**
 * Large Checkbox List Component
 *
 * A component that manages large lists of checkboxes with selected/unselected states.
 * The component is configured using HTML data attributes:
 * - data-checkbox-list: Root container for the component
 * - data-checkbox-list-unselected: Container for unselected checkboxes
 * - data-checkbox-list-selected: Container for selected checkboxes
 * - data-checkbox-list-actions: Container for search and select-all controls
 * - data-checkbox-list-select-all: Button to select all visible checkboxes
 *
 * Features include:
 * - Moving checkboxes between selected and unselected containers
 * - Search filtering for easy navigation
 * - "Select all" functionality for visible checkboxes
 * - Maintains checkbox state during movements
 */

const initLargeCheckboxList = () => {
  const largeCheckboxLists = document.querySelectorAll("[data-checkbox-list]");

  largeCheckboxLists.forEach(function (list) {
    const unselectedContainer = list.querySelector("[data-checkbox-list-unselected]");
    const selectedContainer = list.querySelector("[data-checkbox-list-selected]");
    const actionContainer = list.querySelector("[data-checkbox-list-actions]");

    if (!unselectedContainer || !selectedContainer) {
      console.error("Missing required containers for checkbox list");
      return;
    }

    if (actionContainer) {
      const searchInput = actionContainer.querySelector('input[type="text"]');
      if (searchInput) {
        searchInput.addEventListener("input", function () {
          const searchTerm = this.value.toLowerCase().trim();
          filterCheckboxes(unselectedContainer, searchTerm);
        });

        searchInput.addEventListener("keydown", function (event) {
          if (event.key === "Enter") {
            event.preventDefault();
          }
        });
      }

      const selectAllButton = actionContainer.querySelector("[data-checkbox-list-select-all]");
      if (selectAllButton) {
        selectAllButton.addEventListener("click", function (event) {
          event.preventDefault();
          selectAllVisibleCheckboxes(unselectedContainer);
        });
      }
    }

    setupCheckboxes(unselectedContainer, selectedContainer);
  });

  function selectAllVisibleCheckboxes(container) {
    const checkboxWrappers = container.querySelectorAll(".checkbox-list__list .checkable-container");

    checkboxWrappers.forEach(function (wrapper) {
      // don't select filtered out checkboxes
      if (wrapper.style.display !== "none") {
        const checkbox = wrapper.querySelector('input[type="checkbox"]');
        if (checkbox && !checkbox.checked) {
          checkbox.checked = true;
          checkbox.dispatchEvent(new Event("change"));
        }
      }
    });
  }

  function filterCheckboxes(container, searchTerm) {
    const checkboxWrappers = container.querySelectorAll(".checkbox-list__list .checkable-container");

    checkboxWrappers.forEach(function (label) {
      const checkbox = label.querySelector('input[type="checkbox"]');
      if (!checkbox) return;

      if (label) {
        const labelText = label.textContent.toLowerCase();

        if (searchTerm === "" || labelText.includes(searchTerm)) {
          label.style.display = "";
        } else {
          label.style.display = "none";
        }
      }
    });
  }

  function setupCheckboxes(unselectedContainer, selectedContainer) {
    const unselectedCheckboxes = unselectedContainer.querySelectorAll('input[type="checkbox"]');
    unselectedCheckboxes.forEach(function (checkbox) {
      checkbox.addEventListener("change", function () {
        if (this.checked) {
          moveCheckbox(this, unselectedContainer, selectedContainer);
        }
      });
    });

    const selectedCheckboxes = selectedContainer.querySelectorAll('input[type="checkbox"]');
    selectedCheckboxes.forEach(function (checkbox) {
      checkbox.addEventListener("change", function () {
        if (!this.checked) {
          moveCheckbox(this, selectedContainer, unselectedContainer);
        }
      });
    });
  }

  function moveCheckbox(checkbox, sourceContainer, targetContainer) {
    const checkboxWrapper = checkbox.closest(".checkbox-list__list .checkable-container");

    if (!checkboxWrapper) return;

    const clonedWrapper = checkboxWrapper.cloneNode(true);
    const clonedCheckbox = clonedWrapper.querySelector('input[type="checkbox"]');

    clonedCheckbox.checked = checkbox.checked;

    clonedCheckbox.addEventListener("change", function () {
      if (
        (targetContainer.hasAttribute("data-checkbox-list-selected") && !this.checked) ||
        (targetContainer.hasAttribute("data-checkbox-list-unselected") && this.checked)
      ) {
        moveCheckbox(this, targetContainer, sourceContainer);
      }
    });

    checkboxWrapper.remove();
    targetContainer.appendChild(clonedWrapper);

    const newCheckbox = clonedWrapper.querySelector('input[type="checkbox"]');
    newCheckbox.focus();
  }
};

export default initLargeCheckboxList;
